import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { useLiveData } from "@/context/LiveDataContext";
import { useAuth } from "@/context/AuthContext";
import { StatCard, Dot } from "@/components/common";
import ConfirmDialog from "@/components/ConfirmDialog";
import { rtlConfirmMessage, rtlGate } from "@/lib/rtlSafety";
import { api, apiErr } from "@/lib/api";
import { num, batteryColor, batteryShort, statusColor, timeAgo, gpsLabel, gpsColor, telemetryLabel, telemetryColor } from "@/lib/format";
import { Plane, Wifi, WifiOff, BatteryMedium, Activity, Eye, Send, Home, ArrowUpCircle, Stethoscope } from "lucide-react";
import DroneDiagnosticModal from "@/components/DroneDiagnosticModal";

export default function Dashboard() {
  const { drones, lastEvent } = useLiveData();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rtl, setRtl] = useState(null);
  const [diag, setDiag] = useState(null);

  const total = drones.length;
  const online = drones.filter((d) => d.status === "online").length;
  const offline = total - online;
  const bats = drones.map((d) => d.battery).filter((b) => typeof b === "number" && Number.isFinite(b));
  const avgBat = bats.length ? bats.reduce((s, b) => s + b, 0) / bats.length : null;
  const updatable = drones.filter((d) => d.agent_update_status === "update_available");

  const sendRtl = async (id) => {
    try {
      await api.post(`/drones/${id}/rtl`);
      toast.success("RTL envoyée", { description: id });
    } catch (e) { toast.error("RTL échouée", { description: apiErr(e) }); }
    finally { setRtl(null); }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="font-chivo text-3xl font-black tracking-tighter uppercase">Dashboard Flotte</h1>
        <p className="font-mono text-xs text-[#A3A3A3] mt-1">Vue d'ensemble temps réel de la flotte de drones</p>
      </div>

      {user?.role === "admin" && updatable.length > 0 && (
        <div data-testid="agent-update-banner" className="flex items-center gap-3 border border-[#F59E0B]/40 bg-[#F59E0B]/10 rounded-sm px-4 py-3">
          <ArrowUpCircle size={18} className="text-[#F59E0B] shrink-0" />
          <span className="font-mono text-xs text-[#F59E0B]">
            <span data-testid="agent-update-count" className="font-bold">{updatable.length}</span> drone{updatable.length > 1 ? "s ont" : " a"} une mise à jour agent disponible.
          </span>
          <button data-testid="agent-update-view" onClick={() => navigate("/drone-registry")}
            className="ml-auto flex items-center gap-1 border border-[#F59E0B]/50 text-[#F59E0B] hover:bg-[#F59E0B]/15 rounded-sm font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 transition-colors">
            Voir les drones
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard testid="stat-total" label="Drones total" value={total} icon={Plane} />
        <StatCard testid="stat-online" label="En ligne" value={online} color="#22C55E" icon={Wifi} />
        <StatCard testid="stat-offline" label="Hors ligne" value={offline} color="#525252" icon={WifiOff} />
        <StatCard testid="stat-battery" label="Batterie moy." value={num(avgBat, 0)} unit={avgBat == null ? "" : "%"} color={batteryColor(avgBat)} icon={BatteryMedium} />
        <StatCard testid="stat-last-event" label="Dernier évén." value={lastEvent?.command || lastEvent?.type || "—"} icon={Activity} />
      </div>

      <div>
        <h2 className="font-chivo text-lg font-bold tracking-tight uppercase mb-3">Drones</h2>
        {drones.length === 0 ? (
          <div className="font-mono text-sm text-[#525252] border border-white/10 rounded-sm p-8 text-center">Aucun drone détecté</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {drones.map((d) => (
              <DroneCard key={d.drone_id} d={d}
                onDetails={() => navigate(`/drones?id=${d.drone_id}`)}
                onMission={() => navigate(`/missions?drone=${d.drone_id}`)}
                onDiagnose={() => setDiag(d.drone_id)}
                onRtl={() => setRtl(d.drone_id)} />
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog open={!!rtl} title="RTL Urgence" message={`${rtl} — ${rtlConfirmMessage(drones.find((d) => d.drone_id === rtl))}`}
        danger confirmLabel="RTL" onCancel={() => setRtl(null)} onConfirm={() => sendRtl(rtl)} />

      {diag && <DroneDiagnosticModal droneId={diag} onClose={() => setDiag(null)} />}
    </div>
  );
}

function DroneCard({ d, onDetails, onMission, onDiagnose, onRtl }) {
  const rtlG = rtlGate(d);
  const sc = statusColor(d);
  const offline = d.status !== "online";
  const disabled = d.active === false;
  const diagColor = disabled ? "#A3A3A3" : offline ? "#F59E0B" : "#22C55E";
  const diagLabel = disabled ? "Diag. (désactivé)" : offline ? "Diagnostiquer offline" : "Diagnostic";
  return (
    <div data-testid={`drone-card-${d.drone_id}`} className="bg-[#141414] border border-white/10 rounded-sm p-4 hover:border-white/25 transition-colors">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Dot color={sc} pulse={d.status === "online"} />
          <span className="font-chivo font-bold tracking-tight text-lg">{d.drone_id}</span>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.15em]" style={{ color: sc }}>{d.status || "—"}</span>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-3">
        <Mini label="Batt" value={batteryShort(d)} color={batteryColor(d.battery)} />
        <Mini label="Alt" value={`${num(d.alt)}m`} />
        <Mini label="Vit" value={`${num(d.speed)}`} />
        <Mini label="Mode" value={d.mode || "—"} />
        <Mini label="WS drone" value={d.drone_ws_connected ? "CONNECTÉE" : "OFF"} color={d.drone_ws_connected ? "#22C55E" : "#525252"} />
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        <span data-testid={`telemetry-state-${d.drone_id}`} className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border" style={{ color: telemetryColor(d), borderColor: telemetryColor(d) + "55" }}>{telemetryLabel(d)}</span>
        <span data-testid={`gps-state-${d.drone_id}`} className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border" style={{ color: gpsColor(d), borderColor: gpsColor(d) + "55" }}>{gpsLabel(d)}</span>
      </div>

      <div className="font-mono text-[10px] text-[#525252] mt-2">Dernier signal : {timeAgo(d.last_seen)}</div>

      <div className="grid grid-cols-3 gap-2 mt-3">
        <button data-testid={`details-${d.drone_id}`} onClick={onDetails}
          className="flex items-center justify-center gap-1 border border-white/15 hover:bg-white/10 rounded-sm font-mono text-[10px] uppercase tracking-wider py-2 transition-colors">
          <Eye size={12} /> Détails
        </button>
        <button data-testid={`mission-${d.drone_id}`} onClick={onMission}
          className="flex items-center justify-center gap-1 border border-[#3B82F6]/40 text-[#3B82F6] hover:bg-[#3B82F6]/15 rounded-sm font-mono text-[10px] uppercase tracking-wider py-2 transition-colors">
          <Send size={12} /> Mission
        </button>
        <button data-testid={`rtl-${d.drone_id}`} onClick={onRtl} disabled={!rtlG.allowed}
          title={rtlG.reason || ""}
          className="flex items-center justify-center gap-1 border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444] hover:text-white rounded-sm font-mono text-[10px] uppercase tracking-wider py-2 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#EF4444]">
          <Home size={12} /> RTL
        </button>
      </div>
      {!rtlG.allowed && (
        <div data-testid={`rtl-blocked-reason-${d.drone_id}`} className="mt-1.5 font-mono text-[9px] uppercase tracking-wider text-[#A3A3A3]">
          RTL indisponible : {rtlG.reason}
        </div>
      )}

      <button data-testid={`diagnostic-${d.drone_id}`} onClick={onDiagnose} title={diagLabel}
        className="w-full mt-2 flex items-center justify-center gap-1.5 rounded-sm font-mono text-[10px] uppercase tracking-wider py-2 transition-colors border"
        style={{ borderColor: `${diagColor}66`, color: diagColor }}>
        <Stethoscope size={12} /> {diagLabel}
      </button>
      {disabled && (
        <div className="font-mono text-[9px] text-[#A3A3A3] mt-1.5 text-center">Drone désactivé administrativement</div>
      )}
    </div>
  );
}

function Mini({ label, value, color }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-sm px-2 py-1.5">
      <div className="font-mono text-[8px] uppercase tracking-[0.15em] text-[#525252]">{label}</div>
      <div className="font-mono text-sm font-bold" style={{ color: color || "#fff" }}>{value}</div>
    </div>
  );
}
